

import React, { useState, useEffect } from "react";
import { Eye, EyeOff, LockKeyhole, UserRound } from "lucide-react";
import InputWithIcon from "../../components/ui/InputWithIcon";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import { getAllowedUsers } from "../../api/configHelper";

const LoginPage = () => {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("admin");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const data = await api.CompanyDetails(username);
        if (data?.Table?.length > 0) {
          setCompany(data.Table[0]);
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      }
    };
    fetchCompanyDetails();
  }, [username]);

  const handleLogin = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    const data = await api.checkLogin(username, password);

    const rootKey = Object.keys(data)[0]; 
    const table = data[rootKey]?.Table;

    if (table?.length > 0) {
      const allowedUsers = await getAllowedUsers();
      const allowedList = allowedUsers === "*" ? null : allowedUsers.split(",").map(u => u.trim());

      if (allowedList && !allowedList.includes(username)) {
        setError("You are not allowed to login.");
        setLoading(false);
        return;
      }

      const userData = {
        UsrId: table[0].Usrid,
        UsrName: table[0].UsrName,
        ComName: table[0].comname,
      };
      login(userData); 
    } else {
      setError("Login failed. Please check your credentials.");
    }
  } catch (err) {
    console.error(err);
    setError("An error occurred during login.");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f7faf9] px-4 py-8">
      <div className="w-full max-w-[420px] rounded-2xl border border-slate-200 bg-white px-6 py-8 shadow-lg shadow-slate-900/8 sm:px-9 sm:py-10">
        <div className="mb-8 text-center">
          <div className="mb-5 flex justify-center">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
              <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl bg-white p-2 sm:h-28 sm:w-28">
                <img
                  src="/images/SrbLogo.jpeg"
                  alt={company?.Comname || "Company logo"}
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>

          <h1 className="text-2xl font-semibold tracking-normal text-slate-900">
            {company?.Comname || "Loading..."}
          </h1>
          {company && (
            <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-500">
              {[company.Address1, company.Address2, company.City, company.State, company.Pincode]
                .filter(Boolean)
                .join(", ")}
            </p>
          )}
        </div>

        <form onSubmit={handleLogin} className="space-y-1">
          <InputWithIcon
            label="Username"
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            icon={<UserRound className="h-5 w-5 text-teal-600" />}
          />
          <div className="relative">
            <InputWithIcon
              label="Password"
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              icon={<LockKeyhole className="h-5 w-5 text-teal-600" />}
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="absolute bottom-[5px] right-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-teal-700"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {error && <MessageBox message={error} />}
          <div className="pt-3">
            <Button type="submit" disabled={loading} className="w-full py-3">
              {loading ? "Logging in..." : "Login"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
